import React from "react";
import { FileText, ImageOff, Lock, Trophy } from "lucide-react";
import {
  bulandiGroupEventMinTeammateSlots,
  bulandiGroupEventTeammateSlots,
} from "../../data/bulandiGroupEventRegistration";
import { hasBulandiEventRulesPng } from "../../utils/bulandiEventRulesPng";
import { hasBulandiEventWinnersPng } from "../../utils/bulandiEventWinnersPng";
import {
  isViralVoltageEventId,
  isViralVoltageSignInDisabled,
  BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED_LABEL,
  normalizeInstagramProfileUrl,
} from "../../utils/bulandiViralVoltageRegistration";
import { EventScheduleBadge } from "../pages/bulandi2026/EventScheduleBadge";
import {
  findRegistrationRowByBrNumber,
  getRegistrationDobFromRow,
} from "../../utils/bulandiRegistrationSheet";
import {
  BULANDI_EVENT_SIGN_IN_CLOSED_LABEL,
  isBulandiEventSignInClosed,
} from "../../utils/bulandiEventSignInDeadlines";

/** @param {unknown} entry */
function teammateBr(entry) {
  if (entry && typeof entry === "object" && "br" in entry) {
    return String(entry.br ?? "");
  }
  return String(entry ?? "");
}

/** @param {unknown} entry */
function teammateDob(entry) {
  if (entry && typeof entry === "object" && "dob" in entry) {
    return String(entry.dob ?? "");
  }
  return "";
}

export function BulandiSelectableEventList({
  events,
  eventRegSelectedIds,
  setEventRegSelectedIds,
  eventRegFromSheetIds,
  setEventRegSuccessModalOpen,
  groupTeammateByEventId,
  setGroupTeammateByEventId,
  onClearGroupTeammatesForEvent,
  onViewRules,
  viralVoltageInstagramUrl = "",
  setViralVoltageInstagramUrl,
  viralVoltageInstagramLocked = false,
  registrationRows = [],
  registrationHeaders = [],
}) {
  const hasGroupFields = typeof setGroupTeammateByEventId === "function";
  const hasViralFields = typeof setViralVoltageInstagramUrl === "function";

  const lookupTeammateDob = (brInput) => {
    if (!registrationRows.length || !registrationHeaders.length) return "";
    const match = findRegistrationRowByBrNumber(
      registrationRows,
      registrationHeaders,
      brInput,
    );
    return match ? getRegistrationDobFromRow(match.row, registrationHeaders) : "";
  };

  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {events.map((event) => {
        const checked = eventRegSelectedIds.has(event.id);
        const wasOnRecord = eventRegFromSheetIds.has(event.id);
        const isViralVoltage = isViralVoltageEventId(event.id);
        const viralSignInDisabled = isViralVoltage && isViralVoltageSignInDisabled();
        const lockViralSelection =
          isViralVoltage && (wasOnRecord || viralSignInDisabled);
        const signInClosed = isBulandiEventSignInClosed(event.id);
        const selectionLocked = lockViralSelection || signInClosed;
        const inputId = `bulandi-event-choice-${event.id}`;
        const blurb = String(event.shortDescription || "").trim();
        const iconSrc = String(event.imageUrl || "").trim();
        const teammateSlots = bulandiGroupEventTeammateSlots(event.id);
        const minTeammateSlots = bulandiGroupEventMinTeammateSlots(event.id);
        const hasOptionalTeammate = teammateSlots > minTeammateSlots;
        const teammateValues =
          hasGroupFields && teammateSlots > 0
            ? (() => {
                const raw = groupTeammateByEventId?.[event.id];
                const arr = Array.isArray(raw) ? [...raw] : [];
                while (arr.length < teammateSlots) arr.push({ br: "", dob: "" });
                return arr.slice(0, teammateSlots);
              })()
            : [];
        const hasRules = hasBulandiEventRulesPng(event);
        const hasWinners = hasBulandiEventWinnersPng(event);

        return (
          <li
            key={event.id}
            className={`flex min-h-0 flex-col overflow-hidden rounded-xl border-2 bg-bul-surface/95 shadow-bul-brutal-sm transition ${
              checked
                ? "border-bul-lime ring-1 ring-bul-lime/40"
                : "border-bul-border hover:border-bul-lime/35"
            }`}
          >
            <div className="relative p-3 sm:p-4">
              <div
                className="pointer-events-none absolute right-3 top-3 z-[1] sm:right-4 sm:top-4"
                aria-hidden={true}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-bul-border/80 bg-bul-bg/90 shadow-bul-brutal-sm sm:h-16 sm:w-16">
                  {iconSrc ? (
                    <img
                      src={iconSrc}
                      alt={event.name || "Event icon"}
                      className="max-h-11 max-w-11 object-contain sm:max-h-12 sm:max-w-12"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <ImageOff
                      className="h-7 w-7 text-bul-mutedfg/50 sm:h-8 sm:w-8"
                      strokeWidth={1.75}
                    />
                  )}
                </div>
              </div>
              <label
                htmlFor={inputId}
                className={`flex min-w-0 select-none items-start gap-2.5 pr-[4.25rem] sm:pr-[4.75rem] ${
                  selectionLocked ? "cursor-default" : "cursor-pointer"
                }`}
              >
                <input
                  id={inputId}
                  type="checkbox"
                  checked={checked}
                  disabled={selectionLocked}
                  onChange={() => {
                    if (selectionLocked) return;
                    const wasChecked = eventRegSelectedIds.has(event.id);
                    if (wasChecked) {
                      const prev = hasGroupFields
                        ? groupTeammateByEventId?.[event.id]
                        : undefined;
                      if (hasGroupFields && teammateSlots > 0) {
                        onClearGroupTeammatesForEvent?.(event.id, prev);
                        setGroupTeammateByEventId((g) => {
                          const next = { ...g };
                          delete next[event.id];
                          return next;
                        });
                      }
                      if (
                        isViralVoltage &&
                        hasViralFields &&
                        !viralVoltageInstagramLocked
                      ) {
                        setViralVoltageInstagramUrl("");
                      }
                      setEventRegSelectedIds((prev) => {
                        const n = new Set(prev);
                        n.delete(event.id);
                        return n;
                      });
                    } else {
                      if (hasGroupFields && teammateSlots > 0) {
                        setGroupTeammateByEventId((g) => ({
                          ...g,
                          [event.id]: Array.from({ length: teammateSlots }, () => ({
                            br: "",
                            dob: "",
                          })),
                        }));
                      }
                      setEventRegSelectedIds((prev) => {
                        const n = new Set(prev);
                        n.add(event.id);
                        return n;
                      });
                    }
                    setEventRegSuccessModalOpen(false);
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-bul-border bg-bul-bg text-bul-lime focus:ring-bul-lime disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={`Select ${event.name}`}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex min-w-0 flex-wrap items-start gap-2">
                    <span className="min-w-0 flex-1 break-words font-display text-xs uppercase leading-snug text-bul-fg sm:text-[0.75rem]">
                      {event.name}
                    </span>
                    <EventScheduleBadge eventId={event.id} className="w-fit" />
                  </div>
                  {wasOnRecord ? (
                    <span className="w-fit rounded-full border border-bul-lime/40 bg-bul-lime/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-bul-lime sm:text-xs">
                      Already registered
                    </span>
                  ) : null}
                  {lockViralSelection ? (
                    <span className="flex w-fit items-center gap-1 rounded-full border border-bul-border bg-bul-bg/80 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-bul-mutedfg sm:text-xs">
                      <Lock className="h-3 w-3 shrink-0" aria-hidden />
                      {viralSignInDisabled && !wasOnRecord
                        ? BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED_LABEL
                        : "Locked"}
                    </span>
                  ) : null}
                  {signInClosed ? (
                    <span className="flex w-fit items-center gap-1 rounded-full border border-bul-border bg-bul-bg/80 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-bul-mutedfg sm:text-xs">
                      <Lock className="h-3 w-3 shrink-0" aria-hidden />
                      {BULANDI_EVENT_SIGN_IN_CLOSED_LABEL}
                    </span>
                  ) : null}
                  {blurb ? (
                    <p className="break-words text-xs leading-relaxed text-bul-mutedfg sm:text-sm">
                      {blurb}
                    </p>
                  ) : null}
                </div>
              </label>
            </div>
            {isViralVoltage && checked && hasViralFields ? (
              <div className="border-t border-bul-border/80 px-3 pb-2 pt-1 sm:px-4">
                <label
                  htmlFor="bulandi-viral-voltage-instagram"
                  className="mb-2 block text-[11px] font-mono font-bold uppercase tracking-wide text-bul-mutedfg"
                >
                  Public Instagram profile link{" "}
                  <span className="text-bul-lime">
                    {viralVoltageInstagramLocked ? "" : "*"}
                  </span>
                </label>
                <input
                  id="bulandi-viral-voltage-instagram"
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://instagram.com/yourname"
                  value={viralVoltageInstagramUrl}
                  readOnly={viralVoltageInstagramLocked}
                  disabled={viralVoltageInstagramLocked}
                  onChange={(e) => {
                    if (viralVoltageInstagramLocked) return;
                    setViralVoltageInstagramUrl(e.target.value);
                    setEventRegSuccessModalOpen(false);
                  }}
                  onBlur={(e) => {
                    if (viralVoltageInstagramLocked) return;
                    const normalized = normalizeInstagramProfileUrl(e.target.value);
                    if (normalized && normalized !== e.target.value.trim()) {
                      setViralVoltageInstagramUrl(normalized);
                    }
                  }}
                  className={`w-full rounded-lg border-2 px-3 py-2 font-mono text-sm text-bul-fg placeholder:text-bul-mutedfg/60 focus:outline-none focus:ring-1 ${
                    viralVoltageInstagramLocked
                      ? "cursor-not-allowed border-bul-border/70 bg-bul-bg/60 text-bul-mutedfg"
                      : "border-bul-border bg-bul-bg focus:border-bul-lime focus:ring-bul-lime/40"
                  }`}
                />
                <p className="mt-1.5 text-[11px] leading-snug text-bul-mutedfg">
                  {viralVoltageInstagramLocked
                    ? "Your Instagram link is saved and cannot be changed."
                    : "Required for Viral Voltage. Use your public profile URL."}
                </p>
              </div>
            ) : null}
            {hasGroupFields && teammateSlots > 0 && checked ? (
              <div className="border-t border-bul-border/80 px-3 pb-2 pt-1 sm:px-4">
                <p className="mb-2 text-[11px] font-mono font-bold uppercase tracking-wide text-bul-mutedfg">
                  Teammate B No. &amp; date of birth (
                  {minTeammateSlots === 0
                    ? `optional${teammateSlots > 1 ? `, up to ${teammateSlots}` : ""}`
                    : hasOptionalTeammate
                      ? `${minTeammateSlots} required, ${teammateSlots - minTeammateSlots} optional`
                      : `${teammateSlots} teammate${teammateSlots > 1 ? "s" : ""}`}
                  {" — yours is validated above)"}
                </p>
                <div className="flex flex-col gap-3">
                  {teammateValues.map((entry, idx) => (
                    <div
                      key={`${event.id}-mate-${idx}`}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                    >
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-mono font-bold uppercase tracking-wide text-bul-mutedfg">
                          {`Teammate ${idx + 1} B No.`}
                          {idx >= minTeammateSlots ? " (optional)" : ""}
                        </span>
                        <input
                          type="text"
                          autoComplete="off"
                          readOnly={signInClosed}
                          disabled={signInClosed}
                          value={teammateBr(entry)}
                          onChange={(e) => {
                            if (signInClosed) return;
                            const v = e.target.value;
                            setGroupTeammateByEventId((g) => {
                              const cur = Array.isArray(g[event.id])
                                ? [...g[event.id]]
                                : [];
                              while (cur.length < teammateSlots) {
                                cur.push({ br: "", dob: "" });
                              }
                              const prev = cur[idx] || { br: "", dob: "" };
                              cur[idx] = { ...prev, br: v };
                              return { ...g, [event.id]: cur };
                            });
                            setEventRegSuccessModalOpen(false);
                          }}
                          onBlur={(e) => {
                            if (signInClosed) return;
                            const br = e.target.value.trim();
                            if (!br) return;
                            const dob = lookupTeammateDob(br);
                            if (!dob) return;
                            setGroupTeammateByEventId((g) => {
                              const cur = Array.isArray(g[event.id])
                                ? [...g[event.id]]
                                : [];
                              while (cur.length < teammateSlots) {
                                cur.push({ br: "", dob: "" });
                              }
                              const prev = cur[idx] || { br: "", dob: "" };
                              if (prev.dob) return g;
                              cur[idx] = { ...prev, br, dob };
                              return { ...g, [event.id]: cur };
                            });
                          }}
                          placeholder={`Teammate ${idx + 1} B No.`}
                          className={`w-full rounded-lg border-2 px-3 py-2 font-mono text-sm text-bul-fg placeholder:text-bul-mutedfg/60 focus:outline-none focus:ring-1 ${
                            signInClosed
                              ? "cursor-not-allowed border-bul-border/70 bg-bul-bg/60 text-bul-mutedfg"
                              : "border-bul-border bg-bul-bg focus:border-bul-lime focus:ring-bul-lime/40"
                          }`}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-mono font-bold uppercase tracking-wide text-bul-mutedfg">
                          {`Teammate ${idx + 1} DOB`}
                          {idx >= minTeammateSlots ? " (optional)" : ""}
                        </span>
                        <input
                          type="date"
                          readOnly={signInClosed}
                          disabled={signInClosed}
                          value={teammateDob(entry)}
                          onChange={(e) => {
                            if (signInClosed) return;
                            const v = e.target.value;
                            setGroupTeammateByEventId((g) => {
                              const cur = Array.isArray(g[event.id])
                                ? [...g[event.id]]
                                : [];
                              while (cur.length < teammateSlots) {
                                cur.push({ br: "", dob: "" });
                              }
                              const prev = cur[idx] || { br: "", dob: "" };
                              cur[idx] = { ...prev, dob: v };
                              return { ...g, [event.id]: cur };
                            });
                            setEventRegSuccessModalOpen(false);
                          }}
                          className={`w-full rounded-lg border-2 px-3 py-2 font-mono text-sm text-bul-fg focus:outline-none focus:ring-1 ${
                            signInClosed
                              ? "cursor-not-allowed border-bul-border/70 bg-bul-bg/60 text-bul-mutedfg"
                              : "border-bul-border bg-bul-bg focus:border-bul-lime focus:ring-bul-lime/40"
                          }`}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="flex flex-col gap-3 px-3 pb-3 sm:px-4 sm:pb-4">
              {hasWinners ? (
                <button
                  type="button"
                  onClick={() => onViewRules?.(event)}
                  className="mt-auto flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-bul-border bg-bul-bg/50 px-4 py-2.5 text-center font-mono text-xs font-bold uppercase leading-snug tracking-wide text-bul-lime transition hover:border-bul-lime/50 hover:bg-bul-lime/10 sm:text-sm"
                >
                  <Trophy className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.5} />
                  View winners
                </button>
              ) : hasRules ? (
                <button
                  type="button"
                  onClick={() => onViewRules?.(event)}
                  className="mt-auto flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-bul-border bg-bul-bg/50 px-4 py-2.5 text-center font-mono text-xs font-bold uppercase leading-snug tracking-wide text-bul-lime transition hover:border-bul-lime/50 hover:bg-bul-lime/10 sm:text-sm"
                >
                  <FileText
                    className="h-4 w-4 shrink-0"
                    aria-hidden
                    strokeWidth={2.5}
                  />
                  View rules
                </button>
              ) : (
                <span
                  className="mt-auto flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-bul-border bg-bul-bg/50 px-4 py-2.5 text-center font-mono text-xs font-bold uppercase leading-snug tracking-wide text-bul-mutedfg whitespace-normal sm:text-sm"
                  role="status"
                >
                  {"Rules \u2014 coming soon"}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
